import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ComponentEditModal.css';

const ComponentEditModal = ({ component, type, onClose, onSave, onDelete }) => {
  const isNew = !component;

  // Map API response fields to form fields
  const getInitialFormData = () => {
    if (!component) {
      return {
        name: '',
        code: '',
        isActive: true,
        thumbnailUrl: '',
        modelPath: '',
        hexCode: '#FFFFFF',
        description: '',
        price: '',
      };
    }

    return {
      name: component.productComponentName || component.name || '',
      code: component.productComponentCode || component.code || '',
      isActive: component.isActive !== false,
      thumbnailUrl: component.attributes?.thumbnailUrl || component.thumbnailUrl || '',
      modelPath: component.attributes?.modelPath || component.modelPath || '',
      hexCode: component.attributes?.hexCode || component.hexCode || '#FFFFFF',
      description: component.description || '',
      price: component.price != null ? String(component.price) : '',
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [errors, setErrors] = useState({});
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(
    component?.attributes?.thumbnailUrl || component?.thumbnailUrl || ''
  );
  const [modelFile, setModelFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (thumbnailFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(thumbnailFile);
    }
  }, [thumbnailFile]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (field, file) => {
    if (field === 'thumbnail') {
      setThumbnailFile(file);
    } else if (field === 'model') {
      setModelFile(file);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must not exceed 100 characters';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (formData.code.length > 50) {
      newErrors.code = 'Code must not exceed 50 characters';
    }

    if (type === 'colors') {
      if (!formData.hexCode) {
        newErrors.hexCode = 'Color code is required';
      } else if (!/^#[0-9A-Fa-f]{6}$/.test(formData.hexCode)) {
        newErrors.hexCode = 'Invalid hex color (e.g., #FFFFFF)';
      }
    }

    if (formData.price && (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) < 0)) {
      newErrors.price = 'Price must be a positive number';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must not exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSaving(true);

    try {
      const componentData = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        description: formData.description?.trim() || '',
        isActive: formData.isActive,
        modelPath: formData.modelPath?.trim() || null,
        thumbnailUrl: formData.thumbnailUrl?.trim() || null,
        hexCode: type === 'colors' ? formData.hexCode : null,
        price: formData.price ? parseFloat(formData.price) : null,
      };

      await onSave(componentData);
    } catch (error) {
      console.error('Error saving component:', error);
      setErrors({ submit: 'Failed to save component. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="modal-overlay" onClick={onClose}>
        <motion.div
          className="modal-content"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2 className="modal-title">
              {isNew
                ? `Add New ${type === 'bases' ? 'Base' : type === 'shades' ? 'Shade' : 'Color'}`
                : 'Edit Component'}
            </h2>
            <button className="modal-close" onClick={onClose} disabled={saving}>
              x
            </button>
          </div>

          <form onSubmit={handleSubmit} className="modal-form">
            {errors.submit && (
              <div className="form-error-banner">{errors.submit}</div>
            )}

            <div className="form-section">
              <h3 className="form-section-title">Basic Information</h3>

              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Pillar, Veil, Soft White"
                  maxLength={100}
                  disabled={saving}
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && <span className="error-msg">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="code">Code *</label>
                <input
                  id="code"
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  placeholder="e.g., base-01, shade-01, soft-white"
                  maxLength={50}
                  disabled={!isNew || saving}
                  className={`${!isNew ? 'disabled' : ''} ${errors.code ? 'error' : ''}`}
                />
                {errors.code && <span className="error-msg">{errors.code}</span>}
                {!isNew && !errors.code && (
                  <small className="form-hint">
                    Code cannot be changed after creation
                  </small>
                )}
              </div>

              {type === 'colors' && (
                <div className="form-group">
                  <label htmlFor="hexCode">Color Code *</label>
                  <div className="color-input-group">
                    <input
                      id="hexCode"
                      type="color"
                      value={formData.hexCode}
                      onChange={(e) => handleInputChange('hexCode', e.target.value)}
                      disabled={saving}
                    />
                    <input
                      type="text"
                      value={formData.hexCode}
                      onChange={(e) => handleInputChange('hexCode', e.target.value)}
                      placeholder="#FFFFFF"
                      maxLength={7}
                      disabled={saving}
                      className={errors.hexCode ? 'error' : ''}
                    />
                  </div>
                  {errors.hexCode && <span className="error-msg">{errors.hexCode}</span>}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows="3"
                  placeholder="Optional description..."
                  maxLength={500}
                  disabled={saving}
                  className={errors.description ? 'error' : ''}
                />
                {errors.description && <span className="error-msg">{errors.description}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="price">
                  Price (INR) {type === 'colors' ? '(Premium)' : ''}
                </label>
                <div className="price-input-group">
                  <span className="price-prefix">₹</span>
                  <input
                    id="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder={type === 'colors' ? '0 (for no premium)' : 'e.g., 500'}
                    disabled={saving}
                    className={errors.price ? 'error' : ''}
                  />
                </div>
                {errors.price && <span className="error-msg">{errors.price}</span>}
                {!errors.price && (
                  <small className="form-hint">
                    {type === 'colors'
                      ? 'Additional cost when this color is selected (0 for standard colors)'
                      : 'Base price for this component'}
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    disabled={saving}
                  />
                  <span>Active (visible to users)</span>
                </label>
              </div>
            </div>

            {type !== 'colors' && (
              <>
                <div className="form-section">
                  <h3 className="form-section-title">Thumbnail Image</h3>

                  <div className="file-upload-area">
                    {thumbnailPreview ? (
                      <div className="image-preview">
                        <img src={thumbnailPreview} alt="Thumbnail preview" />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => {
                            setThumbnailPreview('');
                            setThumbnailFile(null);
                            handleInputChange('thumbnailUrl', '');
                          }}
                          disabled={saving}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="file-upload-label">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) =>
                            handleFileChange('thumbnail', e.target.files[0])
                          }
                          style={{ display: 'none' }}
                          disabled={saving}
                        />
                        <div className="file-upload-content">
                          <span className="upload-icon">📷</span>
                          <span>Click to upload thumbnail</span>
                          <small>PNG, JPG up to 2MB</small>
                        </div>
                      </label>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="thumbnailUrl">Or enter image URL</label>
                    <input
                      id="thumbnailUrl"
                      type="text"
                      value={formData.thumbnailUrl}
                      onChange={(e) => {
                        handleInputChange('thumbnailUrl', e.target.value);
                        if (e.target.value && !thumbnailFile) {
                          setThumbnailPreview(e.target.value);
                        }
                      }}
                      placeholder="/images/previews/bases/base_1_preview.png"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="form-section-title">3D Model</h3>

                  <div className="file-upload-area">
                    {modelFile ? (
                      <div className="file-preview">
                        <span className="file-icon">📦</span>
                        <span className="file-name">{modelFile.name}</span>
                        <button
                          type="button"
                          className="remove-file-btn"
                          onClick={() => setModelFile(null)}
                          disabled={saving}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="file-upload-label">
                        <input
                          type="file"
                          accept=".glb,.gltf"
                          onChange={(e) =>
                            handleFileChange('model', e.target.files[0])
                          }
                          style={{ display: 'none' }}
                          disabled={saving}
                        />
                        <div className="file-upload-content">
                          <span className="upload-icon">📦</span>
                          <span>Click to upload 3D model</span>
                          <small>GLB or GLTF format</small>
                        </div>
                      </label>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="modelPath">Or enter model path</label>
                    <input
                      id="modelPath"
                      type="text"
                      value={formData.modelPath}
                      onChange={(e) => handleInputChange('modelPath', e.target.value)}
                      placeholder="/models/bases/base_1.glb"
                      disabled={saving}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="modal-actions">
              {!isNew && onDelete && (
                confirmingDelete ? (
                  <div className="delete-confirm-group">
                    <span className="delete-confirm-text">Are you sure?</span>
                    <button
                      type="button"
                      className="btn-delete-confirm"
                      onClick={() => onDelete(component.id)}
                      disabled={saving}
                    >
                      Yes, Delete
                    </button>
                    <button
                      type="button"
                      className="btn-delete-cancel"
                      onClick={() => setConfirmingDelete(false)}
                      disabled={saving}
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => setConfirmingDelete(true)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                )
              )}
              <div className="modal-actions-right">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving
                    ? 'Saving...'
                    : isNew
                    ? 'Create Component'
                    : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ComponentEditModal;
